/**
 * 業務フロー図の描画エンジンの寸法。
 *
 * **このエンジンは寸法を自分で持つ。** 業務フロー図と、業務ルール側の図・共有の枠は別々の理由で
 * 変わる。寸法を共有すると、片方の見た目を直すたびにもう片方の配置がずれる。値は 2026-09-21 時点の
 * 図（`d2a1aab38`）と同じにしてある — その図がいちばん読みやすかったためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図のレーン帯・升目・ガター・ラベル帯・節点の寸法を定める。
 * @graph-connects none
 */

/**
 * 段（節点 1 行分）の高さ（px）。
 *
 * 節点の高さに加えて、段の下側で線が横に走る水路の帯と、折り返した結末のラベルが積み上がる高さを
 * 含む。縦を厚めに取るのは、この図が横に長く縦に短いためである。縦の余白は図の総面積をほとんど
 * 増やさないが、横を広げると列数ぶん効く。
 * @graph-connects none
 */
export const LANE_HEIGHT = 300;

/** 業務の節点が置ける升目の横幅（px）。 @graph-connects none */
export const NODE_WIDTH = 180;

/**
 * 節点の高さ（px）。線が出入りする縦位置（中心）を決める。
 *
 * 目安ではなく実寸である。節点の側は同じ値を高さの下限にする。食い違うと、線が節点の中心ではない
 * 高さに出入りし、隣の節点を貫いて見える。
 * @graph-connects none
 */
export const NODE_HEIGHT = 104;

/** 並列の分岐・合流（菱形）の一辺（px）。 @graph-connects none */
export const PARALLEL_SHAPE_WIDTH = 64;

/** 開始・終了（錠剤形）の横幅（px）。 @graph-connects none */
export const TERMINAL_SHAPE_WIDTH = 104;

/** 開始・終了（錠剤形）の高さ（px）。 @graph-connects none */
export const TERMINAL_SHAPE_HEIGHT = 34;

/**
 * 業務の節点が 1 つも無い列の升目の横幅（px）。
 *
 * 端の節点・菱形・札しか無い列に業務の升目ぶんの幅を取らない。列が 30 本を超えるフローでは、
 * この差だけで 1 画面に入る業務の節点が 0 個になる。錠剤形の幅に揃えるのは、菱形の名前が図形の
 * 下・升目の幅いっぱいに置かれるためである。
 * @graph-connects none
 */
export const CONTROL_CELL_WIDTH = TERMINAL_SHAPE_WIDTH;

/** ガターの中で最初の縦線を置く位置（升目の右端からの距離）。 @graph-connects none */
export const GUTTER_INSET = 8;

/** ガター内で縦線をずらす量。 @graph-connects none */
export const GUTTER_STEP = 6;

/** ガターのうち、縦線を通す帯の幅。 @graph-connects none */
export const GUTTER_LINE_ZONE = 72;

/** 終点の手前で線が上がり直す帯の幅。 @graph-connects none */
export const GUTTER_RIGHT_RESERVE = 64;

/** 結末のラベルの帯の幅。ラベルはこの幅で折り返す。 @graph-connects none */
export const LABEL_ZONE_WIDTH = 136;

/**
 * 結末のラベルが入るガターの幅。
 *
 * 3 つの帯（縦線・ラベル・上がり直し）と、縦線とラベルを離す隙間の和である。ラベルの帯を両側の帯の
 * 内側に取ることで、ラベルが線に掛からないことが幅の決め方だけで保証される。
 * @graph-connects none
 */
export const GUTTER_WITH_LABEL =
  GUTTER_LINE_ZONE + GUTTER_INSET + LABEL_ZONE_WIDTH + GUTTER_RIGHT_RESERVE;

/**
 * 結末のラベルが入らないガターの幅。
 *
 * その列から出る線にラベルが 1 つも無いなら、ラベルの帯と隙間は要らない。
 * @graph-connects none
 */
export const GUTTER_WITHOUT_LABEL = GUTTER_LINE_ZONE + GUTTER_RIGHT_RESERVE;

/** レーン名の占有幅（px）。節点はこの右から始まる。 @graph-connects none */
export const LANE_LABEL_WIDTH = 184;

/** レーン帯の上端から節点の上端までの距離（px）。 @graph-connects none */
export const NODE_OFFSET_Y = 56;

/** 右端の余白（px）。 @graph-connects none */
export const CANVAS_PADDING_X = 40;
